import type { MaybeRef, MaybeRefOrGetter, ShallowRef, WritableComputedRef } from 'vue';
import { toRef } from '@vueuse/shared';
import { ref, shallowRef, toValue, watch, computed } from 'vue';

export interface UseCycleOptions<T> {
  /**
   * The initial value of the state.
   * A ref can be provided to reuse.
   */
  initialValue?: MaybeRef<T>

  /**
   * The default index when
   */
  fallbackIndex?: number

  /**
   * Custom function to get the index of the current value.
   */
  getIndexOf?: (value: T, list: T[]) => number
}

/**
 * Cycle through a list of items, skipping hidden values when cycling.
 */
export function useCycle<T>(
  list: MaybeRefOrGetter<T[]>,
  hiddenList?: MaybeRefOrGetter<T[]>,
  options?: UseCycleOptions<T>,
): UseCycleReturn<T> {
  const state = shallowRef(getInitialValue()) as ShallowRef<T>;
  const visibleListRef = toRef(list);
  const hiddenListRef = toRef(hiddenList ?? []);
  const _index = ref<number>(0);
  const index = computed<number>({
    get() {
      return _index.value;
    },
    set(value: number) {
      const targetList = visibleListRef.value;
      const length = targetList.length;
      _index.value = (value % length + length) % length;
    },
  });

  function go(i: number, hidden: boolean = false) {
    const targetList = hidden ? hiddenListRef.value : visibleListRef.value;
    if (!hidden) {
      index.value = i;
      i = index.value; // Ensure index is within bounds
    }
    const value = targetList[i];
    state.value = value;
    return value;
  }

  function shift(delta = 1) {
    return go(index.value + delta);
  }

  function next(n = 1) {
    return shift(n);
  }

  function prev(n = 1) {
    return shift(-n);
  }

  function getInitialValue() {
    return toValue(options?.initialValue ?? toValue<T[]>(list)[0]) ?? undefined;
  }

  function set(value: T) {
    // set allows setting any value in the full list, even if hidden
    const targetLists = [...visibleListRef.value, ...hiddenListRef.value];
    if (targetLists.includes(value)) {
      const index = visibleListRef.value.indexOf(value);
      if (index === -1) {
        state.value = value;
      } else {
        go(index, false);
      }
      return value;
    }
    return undefined;
  }

  watch(visibleListRef, () => go(index.value, false));

  return {
    state,
    index,
    next,
    prev,
    set,
    go,
  };
}

export interface UseCycleReturn<T> {
  state: ShallowRef<T>
  index: WritableComputedRef<number>
  next: (n?: number) => T
  prev: (n?: number) => T
  /**
   * Go to a specific index in the visible list (default) or hidden list (if hidden=true)
   */
  go: (i: number, hidden?: boolean) => T
  /**
   * Set the current value to a specific item in the list.
   * Returns undefined if the value is not in the list.
   */
  set: (value: T) => T | undefined,
}
